import React from 'react';
import { Calculator, X, Code2, Copy, Check, Loader2, Settings2, Eye, EyeOff } from 'lucide-react';
import { motion } from 'motion/react';
import { moduleApi } from '../../lib/api';
import { ApercuSimulateur } from '../ui/ApercuSimulateur';
import { designSimulateur, WIDGET_POLICES, WIDGET_ARRONDIS, WIDGET_OMBRES, WIDGET_THEMES, WIDGET_FONDS, type DesignSimulateur } from '../../lib/widgetDesign';
import { SimulateurParcoursSettings, type PrestationSubcategory } from '../Prestations/SimulateurObjets';

// Reglages du simulateur de devis, sortis de la page Parametres et poses dans une modale (demande
// d'Emeni, 2026-09-09) : la carte s'etait allongee au fil des ajouts — activation, public, mode
// d'affichage, question d'accueil, choix multiple, code a coller — et noyait les autres widgets.
// La carte ne garde donc que l'essentiel, tout le reglage vit dans la modale.

type Audience = '' | 'professionnel' | 'particulier';
type Mode = 'liste' | 'guide';
type PhotosMode = 'aucune' | 'facultatif' | 'obligatoire';
import { ChampsPro, CleChampPro, LIBELLES_CHAMPS_PRO, lireChampsPro } from '../../lib/champsDemandeDevisPro';
// Le bouton « apres l'estimation » ne vise plus une Demarche (2026-09-20) : il ouvre la demande de devis.
// `template_id` reste dans le type parce que la colonne existe toujours en base et repart telle quelle au
// serveur — plus rien ne la lit.
interface Cta { enabled: boolean; label: string; template_id: string }

const CARTE = 'p-4 rounded-2xl bg-surface-soft border border-border';
const TITRE_BLOC = 'text-[11px] font-bold text-content-muted uppercase tracking-widest';

function Segment<T extends string>({ value, options, onChange }: {
    value: T;
    options: readonly (readonly [T, string])[];
    onChange: (v: T) => void;
}) {
    return (
        <div className={`grid gap-2`} style={{ gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))` }}>
            {options.map(([v, label]) => (
                <button key={v || 'defaut'} type="button" onClick={() => onChange(v)}
                    className={`rounded-xl border px-2 py-2.5 text-xs font-bold transition-all ${value === v
                        ? 'border-brand-500 bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300'
                        : 'border-border text-content-muted hover:text-content hover:border-content-muted'}`}>
                    {label}
                </button>
            ))}
        </div>
    );
}

// Bouton « copier le lien public » d'un public donné (tous, particuliers, professionnels).
const LienPublicBouton: React.FC<{ label: string; url: string }> = ({ label, url }) => {
    const [ok, setOk] = React.useState(false);
    return (
        <button type="button" disabled={!url}
            onClick={async () => { try { await navigator.clipboard.writeText(url); setOk(true); setTimeout(() => setOk(false), 1800); } catch { /* refusé */ } }}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-border bg-surface hover:bg-surface-soft text-content text-xs font-bold">
            {ok ? <Check size={13} /> : <Copy size={13} />} {ok ? 'Lien copié' : label}
        </button>
    );
};

function SimulateurModal({
    ownerId, enabled, audience, mode, cleApercu, cta, photosMode, champsPro, onChampsPro, minimum, showPrices, objets, categories, countByObjet,
    design, brandColor, onDesign,
    onEnabled, onAudience, onMode, onCta, onPhotosMode, onMinimum, onShowPrices, onParcoursChanged, saving, onClose,
}: {
    ownerId: string;
    design: DesignSimulateur;
    brandColor: string;
    onDesign: (patch: Partial<DesignSimulateur>) => void;
    enabled: boolean;
    audience: Audience;
    mode: Mode;
    cleApercu: number;
    cta: Cta;
    photosMode: PhotosMode;
    champsPro: ChampsPro;
    onChampsPro: (cle: CleChampPro, patch: Partial<{ demande: boolean; obligatoire: boolean }>) => void;
    minimum: string;
    showPrices: boolean;
    objets: PrestationSubcategory[];
    categories: { id: string; name: string }[];
    countByObjet: Record<string, number>;
    onEnabled: (v: boolean) => void;
    onAudience: (v: Audience) => void;
    onMode: (v: Mode) => void;
    onCta: (patch: Partial<Cta>, enregistrer: boolean) => void;
    onPhotosMode: (v: PhotosMode) => void;
    onMinimum: (v: string, enregistrer: boolean) => void;
    onShowPrices: (v: boolean) => void;
    onParcoursChanged: () => void;
    saving: boolean;
    onClose: () => void;
}) {
    const [copie, setCopie] = React.useState(false);
    const snippetPour = (pub: string) => ownerId
        ? `<script src="${window.location.origin}/simulateur-widget.js" data-client-id="${ownerId}"${pub ? ` data-audience="${pub}"` : ''}></script>`
        : '';
    const snippet = snippetPour(audience);
    const snippetTous = snippetPour('');
    const [copieTous, setCopieTous] = React.useState(false);

    const copier = async () => {
        try {
            await navigator.clipboard.writeText(snippet);
            setCopie(true);
            setTimeout(() => setCopie(false), 2000);
        } catch (e) { /* presse-papiers refuse : le code reste selectionnable a la main */ }
    };

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="w-full max-w-4xl bg-surface border border-border rounded-3xl max-h-[90vh] shadow-2xl flex flex-col overflow-hidden"
            >
                <div className="flex items-center justify-between p-6 border-b border-border shrink-0">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="w-10 h-10 rounded-xl bg-brand-500/15 flex items-center justify-center text-brand-500 shrink-0">
                            <Calculator size={19} />
                        </div>
                        <div className="min-w-0">
                            <h3 className="text-base font-bold text-content truncate">Simulateur de devis</h3>
                            <p className="text-[11px] text-content-muted">
                                {saving ? 'Enregistrement...' : 'Chaque changement est enregistre aussitot.'}
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 text-content-muted hover:text-content rounded-xl hover:bg-surface-soft transition-all shrink-0">
                        <X size={19} />
                    </button>
                </div>

                <div className="grid md:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] gap-0 overflow-hidden min-h-0">
                <div className="p-6 space-y-6 overflow-y-auto">
                    <div className={`${CARTE} flex items-center gap-4`}>
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-bold text-content">{enabled ? 'Simulateur actif' : 'Simulateur eteint'}</p>
                            <p className="text-[11px] text-content-muted mt-0.5">
                                {enabled
                                    ? 'Vos visiteurs peuvent estimer un montant depuis votre site.'
                                    : 'Rien n\'est publie : la route publique ne laisse filtrer ni vos tarifs ni votre nom.'}
                            </p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer shrink-0">
                            <input type="checkbox" checked={enabled} className="sr-only peer" onChange={(e) => onEnabled(e.target.checked)} />
                            <div className="w-10 h-6 bg-surface rounded-full border border-border peer-checked:bg-brand-600 transition-all after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-4" />
                        </label>
                    </div>

                    {enabled && (
                        <>
                            <div className="p-3 rounded-xl border border-amber-500/30 bg-amber-500/10">
                                <p className="text-[11px] text-content leading-relaxed">
                                    <b>Vos tarifs deviennent publics.</b> N'importe quel visiteur, concurrent compris, verra
                                    le prix de chaque prestation. C'est le prix de la transparence.
                                </p>
                            </div>

                            {/* Deux simulateurs, chacun sa configuration enregistree (2026-09-22, demande d'Emeni :
                                « une fois configure il bouge pas, et pareil cote pro ») : l'onglet choisi ne
                                change que ce qu'on regle, jamais l'autre public. */}
                            <div className="space-y-2">
                                <p className={TITRE_BLOC}>Quel simulateur regler</p>
                                <Segment<Audience>
                                    value={audience}
                                    options={[['particulier', 'Particuliers'], ['professionnel', 'Professionnels']] as const}
                                    onChange={onAudience}
                                />
                                <p className="text-[11px] text-content-muted">
                                    Chaque onglet garde ses propres reglages (affichage, questions, bouton de devis, photos).
                                    Modifier l'un ne touche jamais a l'autre.
                                </p>
                            </div>

                            {/* Afficher ou masquer les prix (2026-09-25, demande d'Emeni : certains etablissements ne
                                veulent pas afficher leurs tarifs aux professionnels, seulement recevoir leur demande).
                                Sans prix, l'outil change de nature et de nom : ce n'est plus un « simulateur » qui
                                donne un montant, mais une « demande de devis » qui ne fait que transmettre la
                                selection. Le widget public adapte son titre tout seul selon ce reglage. */}
                            <div className="space-y-1.5">
                                <p className={TITRE_BLOC}>Prix affiches a ce public</p>
                                <label className="flex items-start gap-2.5 cursor-pointer">
                                    <input type="checkbox" checked={showPrices} onChange={(e) => onShowPrices(e.target.checked)} className="accent-brand-600 mt-0.5" />
                                    <span className="text-[11px] text-content-muted">
                                        <b className="text-content">Afficher le prix de chaque prestation</b>, le total, et les formules conseillees.
                                        Decochez si {audience === 'professionnel' ? 'vos professionnels' : 'ce public'} ne doivent pas voir vos tarifs :
                                        {audience === 'professionnel' ? ' ils' : ' ils'} pourront quand meme choisir ce qu'ils souhaitent et demander un devis,
                                        sans jamais voir un montant avant votre reponse. Le widget s'appelle alors
                                        « Demande de devis » plutot que « Simulateur de devis ».
                                    </span>
                                </label>
                            </div>

                            <div className="space-y-2">
                                <p className={TITRE_BLOC}>Comment il se presente</p>
                                <Segment<Mode>
                                    value={mode}
                                    options={[['liste', 'Liste complete'], ['guide', 'Une question a la fois']] as const}
                                    onChange={onMode}
                                />
                                <p className="text-[11px] text-content-muted">
                                    {mode === 'liste'
                                        ? 'Tout le catalogue s\'affiche d\'un coup, avec un compteur par prestation.'
                                        : 'Le simulateur demande l\'objet a nettoyer, puis sa variante, puis les options. Creez vos objets dans Prestations > Catalogue — sans eux, la liste complete reste affichee.'}
                                </p>
                            </div>

                            {/* Les questions du parcours se reglent ICI (2026-09-20, demande d'Emeni : « tout
                                doit se faire au meme endroit, sinon on ne comprend plus rien ») — c'est le meme
                                ecran que dans Prestations > Catalogue, pas une copie : un seul composant, deux
                                portes d'entree. */}
                            {mode === 'guide' && (
                                <div className="space-y-1.5 pl-3 border-l-2 border-brand-500/30">
                                    <p className={TITRE_BLOC}>Ses questions</p>
                                    <p className="text-[11px] text-content-muted">
                                        La premiere question et ce qui s'affiche dessous — Canape, Lit, Chaise. Les prestations
                                        rattachees a chaque objet restent, elles, dans <b className="text-content">Prestations &gt; Catalogue</b>.
                                    </p>
                                    <SimulateurParcoursSettings
                                        audience={audience === 'professionnel' ? 'PROFESSIONNEL' : audience === 'particulier' ? 'PRIVÉ' : 'TOUS'}
                                        catalogueModifie={false}
                                        subcategories={objets}
                                        categories={categories}
                                        countByObjet={countByObjet}
                                        onChanged={onParcoursChanged}
                                    />
                                </div>
                            )}

                            <div className="space-y-2">
                                <p className={TITRE_BLOC}>Apres l'estimation</p>
                                <label className="flex items-start gap-2.5 cursor-pointer">
                                    <input type="checkbox" checked={cta.enabled} onChange={(e) => onCta({ enabled: e.target.checked }, true)} className="accent-brand-600 mt-0.5" />
                                    <span className="text-[11px] text-content-muted">
                                        <b className="text-content">Le visiteur peut demander son devis</b> sans quitter votre site : il joint des
                                        photos de ce qu'il a selectionne, laisse ses coordonnees, et vous recevez un dossier a etudier dans
                                        <b className="text-content"> Suivi de prestation</b>, a l'etape « Demande recue ». Particuliers ET
                                        professionnels — la formule declenchee par un pro est reportee sur son devis.
                                    </span>
                                </label>
                                {cta.enabled && (
                                    <div className="space-y-3 pl-3 border-l-2 border-brand-500/30">
                                        <div className="space-y-1.5">
                                            <p className={TITRE_BLOC}>Texte du bouton</p>
                                            <input type="text" value={cta.label} placeholder="Faire ma demande de devis"
                                                onChange={(e) => onCta({ label: e.target.value }, false)}
                                                onBlur={(e) => onCta({ label: e.target.value }, true)}
                                                className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-sm text-content outline-none focus:border-brand-500 transition-colors" />
                                        </div>
                                        <div className="space-y-1.5">
                                            <p className={TITRE_BLOC}>Les photos</p>
                                            <Segment<PhotosMode>
                                                value={photosMode}
                                                options={[['facultatif', 'Proposees'], ['obligatoire', 'Exigees'], ['aucune', 'Pas demandees']] as const}
                                                onChange={onPhotosMode}
                                            />
                                            <p className="text-[11px] text-content-muted">
                                                {photosMode === 'aucune'
                                                    ? 'Le visiteur passe directement a ses coordonnees. A reserver aux prestations que vous chiffrez de toute facon sur place.'
                                                    : photosMode === 'obligatoire'
                                                        ? 'Au moins une photo, sinon la demande n\'est pas envoyee. Vous ne chiffrez jamais a l\'aveugle — mais un visiteur qui n\'a pas de photo sous la main abandonne.'
                                                        : 'Une zone par prestation choisie, trois photos au plus chacune, sans jamais bloquer l\'envoi. C\'est le reglage conseille : la plupart des visiteurs en joignent, et ceux qui n\'en ont pas vous ecrivent quand meme.'}
                                            </p>
                                        </div>
                                        {audience === 'professionnel' && (
                                            <div className="space-y-1.5">
                                                <p className={TITRE_BLOC}>Informations demandees au professionnel</p>
                                                <div className="space-y-1 rounded-xl border border-border p-2.5">
                                                    {LIBELLES_CHAMPS_PRO.map(({ cle, libelle, fixe }) => (
                                                        <div key={cle} className="flex items-center justify-between gap-3">
                                                            <label className="flex items-center gap-2 text-xs text-content cursor-pointer">
                                                                <input type="checkbox" checked={champsPro[cle].demande} disabled={fixe}
                                                                    onChange={(e) => onChampsPro(cle, { demande: e.target.checked })} className="accent-brand-600" />
                                                                {libelle}
                                                            </label>
                                                            {champsPro[cle].demande && (
                                                                <label className="flex items-center gap-1.5 text-[11px] text-content-muted cursor-pointer shrink-0">
                                                                    <input type="checkbox" checked={champsPro[cle].obligatoire} disabled={fixe}
                                                                        onChange={(e) => onChampsPro(cle, { obligatoire: e.target.checked })} className="accent-brand-600" />
                                                                    Obligatoire
                                                                </label>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                                <p className="text-[11px] text-content-muted">
                                                    Seules les informations cochees sont demandees au professionnel. L'email est toujours demande : c'est par lui que part le devis.
                                                </p>
                                            </div>
                                        )}
                                        <div className="space-y-1.5">
                                            <p className={TITRE_BLOC}>Montant minimum d'intervention</p>
                                            <div className="flex items-center gap-2">
                                                <input type="number" min={0} step={5} value={minimum} placeholder="0"
                                                    onChange={(e) => onMinimum(e.target.value, false)}
                                                    onBlur={(e) => onMinimum(e.target.value, true)}
                                                    className="w-28 bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-sm text-content text-right outline-none focus:border-brand-500 transition-colors" />
                                                <span className="text-sm text-content-muted">EUR TTC</span>
                                            </div>
                                            <p className="text-[11px] text-content-muted">
                                                {Number(minimum) > 0
                                                    ? "Sous ce montant, le bouton de demande de devis ne s'affiche pas : le visiteur lit ce qu'il lui manque pour l'atteindre. Le minimum porte sur les prestations simulees, hors frais de deplacement — le simulateur ne les chiffre pas."
                                                    : "0 = aucun minimum, tout le monde peut demander un devis. Mettez un montant si les petites demandes ne valent pas le deplacement."}
                                            </p>
                                        </div>
                                        <p className="text-[11px] text-content-muted">
                                            Le montant simule reste indicatif : la page le rappelle au visiteur a chaque ecran, et c'est vous
                                            qui arretez le tarif reel dans le devis, apres avoir regarde ses photos.
                                        </p>
                                        <p className="text-[11px] text-content-muted">
                                            Les deux emails qui partent alors — l'alerte pour vous, l'accuse de reception pour lui — se
                                            reglent dans <b className="text-content">Parametres &gt; Notifications</b>.
                                        </p>
                                    </div>
                                )}
                            </div>

                            {/* Apparence (2026-10-10, demande d'Emeni) : comme Ma Carte et les autres widgets, le simulateur
                                prend les couleurs et les formes du site qui l'accueille. Un seul reglage pour les deux publics. */}
                            <div className="space-y-3">
                                <p className={TITRE_BLOC}>Apparence sur votre site</p>
                                <p className="text-[11px] text-content-muted">
                                    Couleurs et formes du simulateur, pour qu'il ressemble a votre site. Valable pour les particuliers
                                    comme pour les professionnels ; le site se met a jour tout seul, sans recoller le code.
                                </p>
                                <div className="space-y-1.5">
                                    <p className={TITRE_BLOC}>Couleur principale</p>
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <input type="color" value={design.couleur || brandColor}
                                            onChange={(e) => onDesign({ couleur: e.target.value })}
                                            className="w-11 h-9 rounded-lg border border-border bg-surface cursor-pointer p-0.5" />
                                        <span className="text-xs font-mono text-content-muted">{design.couleur || brandColor}</span>
                                        {design.couleur && (
                                            <button type="button" onClick={() => onDesign({ couleur: '' })}
                                                className="text-[11px] font-bold text-brand-600 hover:text-brand-700 underline">
                                                Reprendre la couleur de ma marque
                                            </button>
                                        )}
                                    </div>
                                </div>
                                <div className="space-y-1.5">
                                    <p className={TITRE_BLOC}>Theme</p>
                                    <Segment<DesignSimulateur['theme']> value={design.theme}
                                        options={WIDGET_THEMES.map((t) => [t.v, t.label] as [DesignSimulateur['theme'], string])}
                                        onChange={(v) => onDesign({ theme: v })} />
                                </div>
                                <div className="space-y-1.5">
                                    <p className={TITRE_BLOC}>Formes des boutons et des cartes</p>
                                    <Segment<DesignSimulateur['arrondi']> value={design.arrondi}
                                        options={WIDGET_ARRONDIS.map((t) => [t.v, t.label] as [DesignSimulateur['arrondi'], string])}
                                        onChange={(v) => onDesign({ arrondi: v })} />
                                </div>
                                <div className="space-y-1.5">
                                    <p className={TITRE_BLOC}>Ombre</p>
                                    <Segment<DesignSimulateur['ombre']> value={design.ombre}
                                        options={WIDGET_OMBRES.map((t) => [t.v, t.label] as [DesignSimulateur['ombre'], string])}
                                        onChange={(v) => onDesign({ ombre: v })} />
                                </div>
                                <div className="space-y-1.5">
                                    <p className={TITRE_BLOC}>Fond de la carte</p>
                                    <Segment<DesignSimulateur['fond']> value={design.fond}
                                        options={WIDGET_FONDS.map((t) => [t.v, t.label] as [DesignSimulateur['fond'], string])}
                                        onChange={(v) => onDesign({ fond: v })} />
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="space-y-1.5">
                                        <p className={TITRE_BLOC}>Police</p>
                                        <select value={design.police} onChange={(e) => onDesign({ police: e.target.value })}
                                            className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-sm text-content outline-none focus:border-brand-500">
                                            {WIDGET_POLICES.map((p) => <option key={p.v || 'site'} value={p.v}>{p.label}</option>)}
                                        </select>
                                    </div>
                                    <div className="space-y-1.5">
                                        <p className={TITRE_BLOC}>Largeur maximale (px)</p>
                                        <input type="number" min={0} max={1600} step={20} value={design.largeur || ''} placeholder="980"
                                            onChange={(e) => onDesign({ largeur: Math.max(0, Math.min(1600, Math.round(Number(e.target.value) || 0))) })}
                                            className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-sm text-content outline-none focus:border-brand-500" />
                                    </div>
                                </div>
                                <p className="text-[11px] text-content-muted">
                                    Un reglage pose dans le code colle sur le site (data-color, data-theme, data-width) l'emporte sur celui-ci.
                                </p>
                            </div>

                            <div className="space-y-2">
                                <p className={TITRE_BLOC}>Le code a coller</p>
                                <div className="bg-surface-soft border border-border rounded-xl p-3 flex items-center gap-2">
                                    <Code2 size={14} className="text-content-muted shrink-0" />
                                    <code className="text-[11px] text-content-muted break-all font-mono flex-1">{snippet}</code>
                                </div>
                                <p className="text-[11px] text-content-muted">
                                    A l'endroit exact ou le simulateur doit apparaitre : contrairement aux autres widgets, il
                                    s'affiche dans la page et non en bouton flottant.
                                </p>
                                <button onClick={copier}
                                    className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg text-xs font-bold transition-all">
                                    {copie ? <><Check size={13} /> Copie</> : <><Copy size={13} /> {audience === 'professionnel' ? 'Copier le code Professionnels' : 'Copier le code Particuliers'}</>}
                                </button>
                                <p className="text-[11px] text-content-muted pt-1">
                                    Une seule page pour les deux publics ? Ce code laisse le visiteur choisir lui-meme :
                                </p>
                                <button onClick={async () => { try { await navigator.clipboard.writeText(snippetTous); setCopieTous(true); setTimeout(() => setCopieTous(false), 2000); } catch { /* refuse */ } }}
                                    className="flex items-center gap-1.5 px-4 py-2 rounded-xl border border-border bg-surface hover:bg-surface-soft text-content text-xs font-bold">
                                    {copieTous ? <><Check size={13} /> Copie</> : <><Copy size={13} /> Copier le code « au choix du visiteur »</>}
                                </button>
                            </div>

                            {/* Lien public (2026-09-22, demande d'Emeni) : sans site internet, ou pour Ma Carte / une
                                bio, le simulateur s'ouvre aussi sur sa propre page. */}
                            <div className="space-y-2">
                                <p className={TITRE_BLOC}>Ou le lien public (sans site internet)</p>
                                <p className="text-[11px] text-content-muted">A mettre sur Ma Carte, en bio Instagram, dans un email ou un SMS.</p>
                                <div className="flex flex-wrap gap-2">
                                    {([['', 'Tous publics'], ['particulier', 'Particuliers'], ['professionnel', 'Professionnels']] as const).map(([pub, lab]) => (
                                        <LienPublicBouton key={pub || 'tous'} label={lab}
                                            url={ownerId ? `${window.location.origin}/simulateur/${ownerId}${pub ? `?public=${pub}` : ''}` : ''} />
                                    ))}
                                </div>
                            </div>
                        </>
                    )}
                </div>

                {/* L'apercu charge le VRAI widget dans une iframe : ce qui s'affiche ici est exactement ce
                    que verra le visiteur. Chaque enregistrement change la cle, ce qui recharge l'iframe —
                    le widget lit ses reglages au chargement, il n'a pas d'autre moyen de se rafraichir. */}
                <div className="border-l border-border bg-surface-soft/40 p-4 flex flex-col min-h-0">
                    <div className="flex items-center gap-2 mb-2 shrink-0">
                        <Eye size={13} className="text-content-muted" />
                        <p className={TITRE_BLOC}>Apercu en direct</p>
                    </div>
                    <div className="flex-1 rounded-2xl border border-border bg-white overflow-hidden min-h-[320px]">
                        <ApercuSimulateur cle={cleApercu} audience={audience} />
                    </div>
                    <p className="text-[11px] text-content-muted mt-2 shrink-0">
                        Visible de vous seul tant que le simulateur n'est pas active.
                    </p>
                </div>
                </div>

                <div className="flex justify-end gap-3 p-6 border-t border-border shrink-0">
                    <button onClick={onClose}
                        className="px-5 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg font-bold text-sm transition-all">
                        Fermer
                    </button>
                </div>
            </motion.div>
        </div>
    );
}

export function SimulateurWidgetSettings({ ownerId }: { ownerId: string }) {
    const [ouvert, setOuvert] = React.useState(false);
    const [chargement, setChargement] = React.useState(true);
    const [saving, setSaving] = React.useState(false);
    const [enabled, setEnabled] = React.useState(false);
    // Onglet en cours, retenu d'une ouverture a l'autre (confort seulement : en cas d'echec, Particuliers).
    const [audience, setAudienceState] = React.useState<Audience>(() => {
        try { return localStorage.getItem('simulateur-onglet') === 'professionnel' ? 'professionnel' : 'particulier'; } catch { return 'particulier'; }
    });
    const setAudience = (v: Audience) => { setAudienceState(v); try { localStorage.setItem('simulateur-onglet', v); } catch { /* navigation privee */ } };
    const estPro = audience === 'professionnel';
    const [modes, setModes] = React.useState<{ particulier: Mode; professionnel: Mode }>({ particulier: 'liste', professionnel: 'liste' });
    const [cleApercu, setCleApercu] = React.useState(0);
    const [design, setDesign] = React.useState<DesignSimulateur>(() => designSimulateur({}));
    const [brandColor, setBrandColor] = React.useState('#987b55');
    const minuteurDesign = React.useRef<ReturnType<typeof setTimeout> | null>(null);
    const [ctas, setCtas] = React.useState<{ particulier: Cta; professionnel: Cta }>({
        particulier: { enabled: false, label: '', template_id: '' }, professionnel: { enabled: false, label: '', template_id: '' },
    });
    const [photos, setPhotos] = React.useState<{ particulier: PhotosMode; professionnel: PhotosMode }>({ particulier: 'facultatif', professionnel: 'facultatif' });
    // Montant minimum d'intervention, par public (2026-09-24, Emeni). Chaine et non nombre : le champ
    // doit pouvoir etre vide pendant la frappe sans retomber a 0 sous les doigts.
    const [minimums, setMinimums] = React.useState<{ particulier: string; professionnel: string }>({ particulier: '0', professionnel: '0' });
    // Afficher les prix, par public (2026-09-25, Emeni) : decoche, l'outil devient une « Demande de
    // devis » plutot qu'un « Simulateur de devis » — voir le widget public.
    const [showPricesByAudience, setShowPricesByAudience] = React.useState<{ particulier: boolean; professionnel: boolean }>({ particulier: true, professionnel: true });
    const [champsPro, setChampsPro] = React.useState<ChampsPro>(() => lireChampsPro(null));
    const cle = estPro ? 'professionnel' : 'particulier';
    const mode = modes[cle];
    const cta = ctas[cle];
    const photosMode = photos[cle];
    const showPrices = showPricesByAudience[cle];

    // Objets du parcours guide et catalogue : charges ici pour que les questions du simulateur se reglent
    // dans l'ecran du simulateur (2026-09-20) — le meme composant sert dans Prestations > Catalogue.
    const [objets, setObjets] = React.useState<PrestationSubcategory[]>([]);
    const [categories, setCategories] = React.useState<{ id: string; name: string }[]>([]);
    const [countByObjet, setCountByObjet] = React.useState<Record<string, number>>({});

    const chargerParcours = React.useCallback(() => {
        moduleApi.getPrestationSubcategories().then((r: any) => setObjets(Array.isArray(r) ? r : [])).catch(() => setObjets([]));
        moduleApi.getPrestationCategories().then((r: any) => setCategories(Array.isArray(r) ? r : [])).catch(() => setCategories([]));
        // Le nombre de prestations rattachees a chaque objet : c'est ce qui dit a l'ecran des questions
        // qu'un objet est vide et ne posera donc aucune question.
        moduleApi.getBillingSettings().then((r: any) => {
            let items: any[] = [];
            try { items = Array.isArray(r?.prestations_catalog) ? r.prestations_catalog : JSON.parse(r?.prestations_catalog || '[]'); } catch (e) { items = []; }
            setCountByObjet(items.reduce((acc: Record<string, number>, it: any) => {
                if (it && it.kind !== 'option' && it.subcategory_id) acc[it.subcategory_id] = (acc[it.subcategory_id] || 0) + 1;
                return acc;
            }, {}));
        }).catch(() => setCountByObjet({}));
    }, []);

    React.useEffect(() => {
        moduleApi.getPrestationPlanningSettings().then((s: any) => {
            setEnabled(!!s?.simulateur_enabled);
            const m = (v: any): Mode => v === 'guide' ? 'guide' : 'liste';
            const ph = (v: any): PhotosMode => ['aucune', 'obligatoire'].includes(v) ? v : 'facultatif';
            setModes({ particulier: m(s?.simulateur_mode), professionnel: m(s?.simulateur_mode_pro) });
            setCtas({
                particulier: { enabled: !!s?.simulateur_cta_enabled, label: s?.simulateur_cta_label || '', template_id: s?.simulateur_cta_template_id || '' },
                professionnel: { enabled: !!s?.simulateur_cta_enabled_pro, label: s?.simulateur_cta_label_pro || '', template_id: '' },
            });
            setPhotos({ particulier: ph(s?.simulateur_photos_mode), professionnel: ph(s?.simulateur_photos_mode_pro) });
            setChampsPro(lireChampsPro(s?.simulateur_pro_champs));
            setMinimums({ particulier: String(Number(s?.simulateur_minimum_ttc) || 0), professionnel: String(Number(s?.simulateur_minimum_ttc_pro) || 0) });
            setShowPricesByAudience({
                particulier: s?.simulateur_show_prices !== false,
                professionnel: s?.simulateur_show_prices_pro !== false,
            });
        }).catch(() => {}).finally(() => setChargement(false));
        moduleApi.getSimulateurDesign().then((r: any) => setDesign(designSimulateur(r?.design))).catch(() => {});
        // Couleur de la marque : affichee tant qu'aucune couleur propre au simulateur n'est choisie.
        moduleApi.getSimulateurApercu({}).then((r: any) => { if (r?.brand_color) setBrandColor(r.brand_color); }).catch(() => {});
        chargerParcours();
    }, [chargerParcours]);

    // Le selecteur de couleur emet en continu : on attend une demi-seconde de calme avant d'enregistrer.
    const majDesign = (patch: Partial<DesignSimulateur>) => {
        const suivant = designSimulateur({ ...design, ...patch });
        setDesign(suivant);
        if (minuteurDesign.current) clearTimeout(minuteurDesign.current);
        minuteurDesign.current = setTimeout(async () => {
            setSaving(true);
            try { await moduleApi.saveSimulateurDesign(suivant); } catch (e) { console.error('Failed to save simulateur design', e); }
            finally { setSaving(false); setCleApercu((k) => k + 1); }
        }, 500);
    };

    // Chaque onglet ecrit ses propres colonnes : suffixe _pro pour l'onglet Professionnels.
    const enregistrer = async (patch: Record<string, unknown>) => {
        setSaving(true);
        try {
            await moduleApi.savePrestationPlanningSettings(patch);
        } catch (e) {
            console.error('Failed to save simulateur setting', e);
        } finally {
            setSaving(false);
            setCleApercu((k) => k + 1);
        }
    };

    if (chargement) return null;

    return (
        <>
            <div className={`${CARTE} space-y-3`}>
                <div className="flex items-center gap-4">
                    <div className="w-11 h-11 rounded-xl bg-brand-500/15 flex items-center justify-center text-brand-500 shrink-0">
                        <Calculator size={20} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-bold text-content text-sm">Simulateur de devis</p>
                            <span className="text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded bg-surface border border-border text-content-muted">Dans la page</span>
                        </div>
                        <p className="text-xs text-content-muted mt-0.5">
                            Vos visiteurs estiment eux-memes le montant d'une prestation, a partir de votre catalogue et
                            de vos formules. Rien a remplir, rien a envoyer : un prix s'affiche, c'est tout.
                        </p>
                    </div>
                </div>
                <div className="flex items-center justify-between gap-3">
                    <span className={`inline-flex items-center gap-1.5 text-[11px] font-bold ${enabled ? 'text-emerald-600 dark:text-emerald-400' : 'text-content-muted'}`}>
                        {enabled ? <Eye size={13} /> : <EyeOff size={13} />}
                        {enabled ? 'Actif — particuliers et professionnels reglés separement' : 'Eteint'}
                    </span>
                    <button onClick={() => setOuvert(true)}
                        className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-surface border border-border hover:border-brand-500 text-content text-xs font-bold transition-all">
                        {saving ? <Loader2 size={13} className="animate-spin" /> : <Settings2 size={13} />} Configurer
                    </button>
                </div>
            </div>

            {ouvert && (
                <SimulateurModal
                    ownerId={ownerId}
                    design={design}
                    brandColor={brandColor}
                    onDesign={majDesign}
                    enabled={enabled}
                    audience={audience}
                    mode={mode}
                    cleApercu={cleApercu}
                    cta={cta}
                    photosMode={photosMode}
                    champsPro={champsPro}
                    onChampsPro={(k, patch) => {
                        const suivant = lireChampsPro({ ...champsPro, [k]: { ...champsPro[k], ...patch } });
                        setChampsPro(suivant);
                        enregistrer({ simulateur_pro_champs: suivant });
                    }}
                    onPhotosMode={(v) => { setPhotos((p) => ({ ...p, [cle]: v })); enregistrer({ [estPro ? 'simulateur_photos_mode_pro' : 'simulateur_photos_mode']: v }); }}
                    minimum={minimums[cle]}
                    onMinimum={(v, sauver) => {
                        setMinimums((m) => ({ ...m, [cle]: v }));
                        if (sauver) enregistrer({ [estPro ? 'simulateur_minimum_ttc_pro' : 'simulateur_minimum_ttc']: Math.max(0, Number(v) || 0) });
                    }}
                    showPrices={showPrices}
                    onShowPrices={(v) => {
                        setShowPricesByAudience((p) => ({ ...p, [cle]: v }));
                        enregistrer({ [estPro ? 'simulateur_show_prices_pro' : 'simulateur_show_prices']: v });
                    }}
                    objets={objets.filter((sc) => {
                        const scope = (sc as any).client_type_scope || 'TOUS';
                        if (audience === 'professionnel') return scope === 'TOUS' || scope === 'PROFESSIONNEL';
                        if (audience === 'particulier') return scope === 'TOUS' || scope === 'PRIVÉ';
                        return true;
                    })}
                    categories={categories}
                    countByObjet={countByObjet}
                    onParcoursChanged={() => { chargerParcours(); setCleApercu((k) => k + 1); }}
                    onCta={(patch, ecrire) => {
                        const suivant = { ...cta, ...patch };
                        setCtas((c) => ({ ...c, [cle]: suivant }));
                        if (!ecrire) return;
                        enregistrer(estPro
                            ? { simulateur_cta_enabled_pro: suivant.enabled, simulateur_cta_label_pro: suivant.label }
                            : { simulateur_cta_enabled: suivant.enabled, simulateur_cta_label: suivant.label, simulateur_cta_template_id: suivant.template_id });
                    }}
                    saving={saving}
                    onEnabled={(v) => { setEnabled(v); enregistrer({ simulateur_enabled: v }); }}
                    onAudience={(v) => { setAudience(v); setCleApercu((k) => k + 1); }}
                    onMode={(v) => { setModes((p) => ({ ...p, [cle]: v })); enregistrer({ [estPro ? 'simulateur_mode_pro' : 'simulateur_mode']: v }); }}
                    onClose={() => setOuvert(false)}
                />
            )}
        </>
    );
}
